import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import {
  Calendar, Clock, User, Share2,
  Video, FileText, MessageSquare, Search, ChevronDown,
  CheckCircle2, Loader2, ExternalLink, Check, Copy, X,
  ArrowLeft, Filter, Play, ChevronRight, AlertTriangle,
} from 'lucide-react';

const API_BASE_URL = (process.env.REACT_APP_API_URL || "").replace("http://", "https://");
const SUMMARIZER_PROXY_URL = `${API_BASE_URL}/api/summarizer`;

// --- HELPER: Date ---
const getMeetingDate = (meeting) => {
  if (!meeting) return new Date();
  const dateStr = meeting.start_time || meeting.engaged_at || meeting.createdAt;
  return dateStr ? new Date(dateStr) : new Date();
};

// --- HELPER: Transcript Parser ---
const parseTranscriptData = (rawEvents) => {
  if (!rawEvents || !Array.isArray(rawEvents)) return [];
  const processedEvents = [];
  let lastKnownSpeaker = "Unknown";

  rawEvents.forEach((evt, evtIndex) => {
    const rawText = evt.text || "";
    if (evt.speaker && evt.speaker !== 'Unknown' && !rawText.includes('\n')) {
      lastKnownSpeaker = evt.speaker;
      processedEvents.push({ ...evt, displaySpeaker: evt.speaker, displayText: rawText });
      return;
    }
    const lines = rawText.split('\n');
    let currentBuffer = [];
    const nameRegex = /^[A-Z][a-zA-Z\s.-]+$/;

    lines.forEach((line, lineIndex) => {
      const trimmed = line.trim();
      if (!trimmed) return;
      const isName = nameRegex.test(trimmed) && trimmed.length < 30 && trimmed.split(' ').length <= 5;
      if (isName) {
        if (currentBuffer.length > 0) {
          processedEvents.push({ ...evt, id: `${evt.id}_part_${evtIndex}_${lineIndex}`, displaySpeaker: lastKnownSpeaker, displayText: currentBuffer.join('\n'), speaker: lastKnownSpeaker });
        }
        lastKnownSpeaker = trimmed;
        currentBuffer = [];
      } else {
        currentBuffer.push(line);
      }
    });
    if (currentBuffer.length > 0) {
      processedEvents.push({ ...evt, id: `${evt.id}_final_${evtIndex}`, displaySpeaker: lastKnownSpeaker, displayText: currentBuffer.join('\n'), speaker: lastKnownSpeaker });
    }
  });
  return processedEvents;
};

// --- HELPER: Markdown Summary Renderer ---
const renderMarkdownSummary = (markdownText, darkMode) => {
  if (!markdownText) return null;

  const lines = markdownText.split('\n');
  const elements = [];
  let currentList = [];
  let key = 0;

  const flushList = () => {
    if (currentList.length > 0) {
      elements.push(
        <ul key={`list-${key++}`} className={`space-y-2 mb-6 ml-6 ${darkMode ? 'text-slate-300' : 'text-slate-700'}`}>
          {currentList.map((item, idx) => (
            <li key={idx} className="text-sm leading-relaxed pl-2 relative before:content-['•'] before:absolute before:left-[-1rem] before:text-secondary before:font-medium">
              {item}
            </li>
          ))}
        </ul>
      );
      currentList = [];
    }
  };

  lines.forEach((line, i) => {
    const trimmed = line.trim();

    // Skip empty lines
    if (!trimmed) {
      flushList();
      return;
    }

    // Handle headings: ## N. **Title** or ## Title
    const headingMatch = trimmed.match(/^#{1,6}\s+(\d+\.\s*)?(\*\*)?(.+?)(\*\*)?$/);
    if (headingMatch) {
      flushList();
      const number = headingMatch[1] || '';
      const titleText = headingMatch[3].replace(/\*\*/g, '');

      elements.push(
        <div key={`heading-${i}`} className={`flex items-baseline gap-3 mb-4 mt-8 first:mt-0 pb-3 border-b ${darkMode ? 'border-white/10' : 'border-slate-200'}`}>
          {number && (
            <span className={`text-2xl font-medium ${darkMode ? 'text-secondary' : 'text-secondary'}`}>
              {number.replace('.', '')}
            </span>
          )}
          <h3 className={`text-lg font-bold ${darkMode ? 'text-white' : 'text-slate-900'}`}>
            {titleText}
          </h3>
        </div>
      );
      return;
    }

    // Handle list items
    if (trimmed.startsWith('-')) {
      const content = trimmed.substring(1).trim();
      // Parse bold within list items
      const parts = content.split(/(\*\*.*?\*\*)/g);
      const rendered = parts.map((part, idx) => {
        if (part.startsWith('**') && part.endsWith('**')) {
          return <strong key={idx} className={darkMode ? 'text-white' : 'text-slate-900'}>{part.slice(2, -2)}</strong>;
        }
        return part;
      });
      currentList.push(rendered);
      return;
    }

    // Handle regular paragraphs
    flushList();
    if (trimmed) {
      // Parse bold text in paragraphs
      const parts = trimmed.split(/(\*\*.*?\*\*)/g);
      const rendered = parts.map((part, idx) => {
        if (part.startsWith('**') && part.endsWith('**')) {
          return <strong key={idx} className={darkMode ? 'text-white' : 'text-slate-900'}>{part.slice(2, -2)}</strong>;
        }
        return part;
      });

      elements.push(
        <p key={`p-${i}`} className={`text-sm leading-relaxed mb-3 ${darkMode ? 'text-slate-400' : 'text-slate-600'}`}>
          {rendered}
        </p>
      );
    }
  });

  flushList(); // Flush any remaining list items

  return <div className="space-y-1">{elements}</div>;
};

// =========================================================
// SUB-COMPONENT: Todo Item with Expanded Team Search
// =========================================================
const TodoItemWithAssign = ({ todo, meetingId, meetingTitle, teamMembers, darkMode, onJumpToSource }) => {
  const [isAssigning, setIsAssigning] = useState(false);
  const [assignedUsers, setAssignedUsers] = useState([]);
  const [isAssignedInDb, setIsAssignedInDb] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");

  useEffect(() => {
    const checkExistingTask = async () => {
      try {
        const token = localStorage.getItem('token');
        const description = typeof todo === 'object' ? (todo.task || todo.text) : todo;
        const res = await fetch(`${API_BASE_URL}/api/tasks?meetingId=${meetingId}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (res.ok) {
          const data = await res.json();
          const existingTask = data.data?.find(task =>
            task.description === description ||
            task.description?.includes(description) ||
            description?.includes(task.description)
          );
          if (existingTask) {
            setIsAssignedInDb(true);
            if (existingTask.assignees && existingTask.assignees.length > 0) {
              setAssignedUsers(existingTask.assignees.map(a => ({
                _id: a.user_id,
                name: a.name,
                email: a.email || ''
              })));
            }
          }
        }
      } catch (error) { console.error('Error checking existing task:', error); }
    };
    if (meetingId) checkExistingTask();
  }, [meetingId, todo]);

  const filteredTeam = (teamMembers || []).filter(member =>
    (member.name || "").toLowerCase().includes(searchTerm.toLowerCase()) ||
    (member.email || "").toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleConfirmAssignment = async () => {
    if (assignedUsers.length === 0) return;
    setIsAssignedInDb(true);
    setIsAssigning(false);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_BASE_URL}/api/tasks`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({
          description: typeof todo === 'object' ? (todo.task || todo.text) : todo,
          meetingId: meetingId,
          meetingTitle: meetingTitle,
          assignees: assignedUsers.map(u => ({ user_id: u._id || u.id, name: u.name, email: u.email }))
        })
      });
      if (!res.ok) {
        const errorData = await res.json();
        throw new Error(errorData.error || errorData.message || "Server rejected assignment");
      }
    } catch (error) {
      console.error("Assignment Error:", error);
      setIsAssignedInDb(false);
      alert(`Failed to assign task: ${error.message}`);
    }
  };

  const toggleUser = (user) => {
    const userId = user._id || user.id;
    if (assignedUsers.find(u => (u._id || u.id) === userId)) {
      setAssignedUsers(prev => prev.filter(u => (u._id || u.id) !== userId));
    } else {
      setAssignedUsers(prev => [...prev, user]);
    }
  };

  const todoObj = typeof todo === 'object' ? todo : { task: String(todo) };
  const todoText = String(todoObj.task || todoObj.text || todo).replace(/\*\*/g, '').trim();
  const hasSource = todoObj.event_ids && todoObj.event_ids.length > 0;

  return (
    <div className={`group relative flex flex-col gap-2 py-3 px-4 rounded-xl transition-all border ${darkMode ? 'bg-white/5 border-white/10' : 'bg-gray-50 border-gray-200'}`}>
      <div className="flex items-start gap-3">
        <div className={`mt-0.5 w-5 h-5 rounded border-2 shrink-0 flex items-center justify-center transition-colors ${isAssignedInDb ? 'bg-green-500 border-green-500' : (darkMode ? 'border-white/30' : 'border-gray-300')}`}>
          {isAssignedInDb && <Check size={12} className="text-white" />}
        </div>
        <div className="flex-1 min-w-0">
          <div className={`${hasSource ? 'cursor-pointer hover:underline' : ''}`} onClick={() => hasSource && onJumpToSource()}>
            <p className={`text-sm leading-relaxed ${darkMode ? 'text-white' : 'text-slate-800'}`}>
              {todoObj.user && <span className="font-bold text-blue-500 mr-2">{todoObj.user}</span>}
              {todoText}
            </p>
          </div>
          {isAssignedInDb && (
            <div className="flex items-center gap-2 mt-2 animate-in fade-in">
              <div className="flex -space-x-1.5">
                {assignedUsers.map((u, i) => (
                  <div key={i} className={`w-5 h-5 rounded-full bg-blue-600 border border-white text-[8px] flex items-center justify-center text-white font-bold`}>{(u.name || "U")[0]}</div>
                ))}
              </div>
              <span className="text-[10px] opacity-50 font-bold text-green-500">Promoted to Task</span>
            </div>
          )}
        </div>
        {hasSource && <ExternalLink size={14} className="opacity-30" />}
      </div>
      {!isAssignedInDb && (
        <div className="pl-8">
          {!isAssigning ? (
            <button onClick={() => setIsAssigning(true)} className={`flex items-center gap-1 text-xs font-bold px-2 py-1 rounded-md transition-colors ${darkMode ? 'text-blue-400 bg-blue-500/10 hover:bg-blue-500/20' : 'text-blue-600 bg-blue-50 hover:bg-blue-100'}`}>
              <User size={12} /> Assign
            </button>
          ) : (
            <div className={`w-full max-w-xs p-3 rounded-xl border mt-2 z-20 relative animate-in fade-in slide-in-from-top-1 ${darkMode ? 'bg-[#1F2833] border-white/10' : 'bg-white border-gray-200 shadow-xl'}`}>
              <div className={`flex items-center gap-2 px-2 py-2 rounded-lg border mb-2 ${darkMode ? 'bg-black/20 border-white/10' : 'bg-gray-50 border-gray-200'}`}>
                <Search size={14} className="opacity-50" />
                <input autoFocus placeholder="Search team..." className={`bg-transparent text-sm w-full outline-none ${darkMode ? 'text-white' : 'text-slate-900'}`} value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} />
              </div>
              <div className="max-h-60 overflow-y-auto custom-scrollbar space-y-1">
                {filteredTeam.length === 0 ? <div className="text-center text-[10px] opacity-50 py-4">No members found</div> :
                  filteredTeam.map(user => {
                    const userId = user._id || user.id;
                    const isSelected = assignedUsers.find(u => (u._id || u.id) === userId);
                    return (
                      <button key={userId} onClick={() => toggleUser(user)} className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg text-sm transition-colors ${isSelected ? 'bg-blue-500/20 text-blue-500' : (darkMode ? 'hover:bg-white/5' : 'hover:bg-gray-50')}`}>
                        <div className={`w-6 h-6 rounded-full bg-indigo-500 flex items-center justify-center text-white text-[10px] font-bold shrink-0`}>{(user.name || "U")[0]}</div>
                        <div className="flex-1 text-left min-w-0">
                          <div className="truncate font-medium">{user.name}</div>
                          <div className="text-[10px] opacity-50 truncate">{user.email}</div>
                        </div>
                        {isSelected && <Check size={14} />}
                      </button>
                    )
                  })
                }
              </div>
              <div className="flex gap-2 mt-3 pt-2 border-t border-dashed border-gray-500/20">
                <button onClick={() => setIsAssigning(false)} className="flex-1 py-1.5 text-xs opacity-60 hover:opacity-100 font-medium">Cancel</button>
                <button onClick={handleConfirmAssignment} disabled={assignedUsers.length === 0} className="flex-1 py-1.5 bg-blue-600 text-white rounded-lg text-xs font-bold hover:bg-blue-500 disabled:opacity-50 disabled:bg-gray-500">Confirm</button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// =========================================================
// MAIN NOTES COMPONENT
// =========================================================
const Notes = ({ darkMode, user, calendarEvents }) => {
  const [selectedMeetingId, setSelectedMeetingId] = useState('');
  const [transcriptData, setTranscriptData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [engagedMeetings, setEngagedMeetings] = useState([]);
  const [engagedLoading, setEngagedLoading] = useState(true);
  const [activeSourceIds, setActiveSourceIds] = useState([]);
  const [activeBlockIndex, setActiveBlockIndex] = useState(null);
  const [activeTab, setActiveTab] = useState('notes');
  const [teamMembers, setTeamMembers] = useState([]);

  const [showMobileTranscript, setShowMobileTranscript] = useState(false);
  const [isMeetingDropdownOpen, setIsMeetingDropdownOpen] = useState(false);
  const dropdownRef = useRef(null);

  // Export & Share State
  const [showExportMenu, setShowExportMenu] = useState(false);
  const [integrationStatus, setIntegrationStatus] = useState({ notion: { connected: false }, slack: { connected: false } });
  const [exportingNotion, setExportingNotion] = useState(false);
  const [exportingSlack, setExportingSlack] = useState(false);
  const [exportSuccessUrl, setExportSuccessUrl] = useState(null);
  const [showDestinationPicker, setShowDestinationPicker] = useState(null);
  const [exportSuccessProvider, setExportSuccessProvider] = useState(null);
  const [showShareModal, setShowShareModal] = useState(false);
  const [shareUrl, setShareUrl] = useState('');
  const [isSharing, setIsSharing] = useState(false);
  const [copied, setCopied] = useState(false);

  const [sidebarWidth, setSidebarWidth] = useState(400);
  const [isResizing, setIsResizing] = useState(false);

  const transcriptRefs = useRef({});
  const videoRef = useRef(null);
  const dataCache = useRef({});

  useEffect(() => {
    const fetchTeam = async () => {
      try {
        const token = localStorage.getItem('token');
        const res = await fetch(`${API_BASE_URL}/api/team`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (res.ok) {
          const data = await res.json();
          const me = { _id: user?._id || user?.id, name: "Me (You)", email: user?.email };
          const others = (data.team || []).filter(m => (m._id || m.id) !== (user?._id || user?.id));
          setTeamMembers([me, ...others]);
        } else {
          setTeamMembers([{ _id: user?._id || user?.id, name: "Me (You)", email: user?.email }]);
        }
      } catch (e) { console.error("Failed to load team", e); }
    };
    if (user) fetchTeam();
  }, [user]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) setIsMeetingDropdownOpen(false);
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const startResizing = useCallback(() => setIsResizing(true), []);
  const stopResizing = useCallback(() => setIsResizing(false), []);
  const resize = useCallback((e) => {
    if (isResizing) {
      const newWidth = window.innerWidth - e.clientX;
      if (newWidth > 300 && newWidth < 800) setSidebarWidth(newWidth);
    }
  }, [isResizing]);

  useEffect(() => {
    window.addEventListener("mousemove", resize);
    window.addEventListener("mouseup", stopResizing);
    return () => {
      window.removeEventListener("mousemove", resize);
      window.removeEventListener("mouseup", stopResizing);
    };
  }, [resize, stopResizing]);

  useEffect(() => {
    const fetchEngagedMeetings = async () => {
      try {
        const token = localStorage.getItem('token');
        if (!token) { setEngagedLoading(false); return; }
        const res = await fetch(`${API_BASE_URL}/api/meetings`, { headers: { 'Authorization': `Bearer ${token}` } });
        if (res.ok) {
          const data = await res.json();
          if (data.success) {
            const now = Date.now();
            const completedMeetings = data.data.filter(m => {
              const endTime = m.end_time || m.last_stopped_at || m.updatedAt;
              return (endTime && new Date(endTime).getTime() < now) || (m.meeting_title && m.meetlink);
            });
            const sorted = completedMeetings.sort((a, b) => getMeetingDate(b) - getMeetingDate(a));
            setEngagedMeetings(sorted);
            if (window.innerWidth >= 768 && sorted.length > 0 && !selectedMeetingId) {
              setSelectedMeetingId(sorted[0].meeting_id);
            }
          }
        }
      } catch (error) { console.error(error); } finally { setEngagedLoading(false); }
    };
    fetchEngagedMeetings();
  }, []);

  const activeMeeting = selectedMeetingId ? engagedMeetings.find(m => m.meeting_id === selectedMeetingId) : engagedMeetings[0];

  const saveMeetingTitle = async (meetingId, newTitle) => {
    if (!meetingId || !newTitle) return;
    try {
      const token = localStorage.getItem('token');
      await fetch(`${API_BASE_URL}/api/meetings/${meetingId}`, {
        method: 'PATCH',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ meeting_title: newTitle })
      });
      setEngagedMeetings(prev => prev.map(m => m.meeting_id === meetingId ? { ...m, meeting_title: newTitle } : m));
    } catch (e) { console.error(e); }
  };

  useEffect(() => {
    const fetchStatus = async () => {
      const token = localStorage.getItem('token');
      if (!token) return;
      try {
        const res = await fetch(`${API_BASE_URL}/oauth/status`, { headers: { 'Authorization': `Bearer ${token}` } });
        if (res.ok) {
          const data = await res.json();
          if (data.success && data.data) {
            setIntegrationStatus({
              notion: data.data.notion || { connected: false },
              slack: data.data.slack || { connected: false }
            });
          }
        }
      } catch (e) { }
    };
    fetchStatus();
  }, []);

  const generateShareLink = async () => {
    if (!activeMeeting) { console.warn("No active meeting selected to share."); return; }
    setIsSharing(true);
    try {
      const idToShare = activeMeeting._id || activeMeeting.meeting_id;
      const payload = {
        title: displayTitle,
        content: transcriptData?.summary || "Summary not available yet.",
        todos: transcriptData?.todos || []
      };
      const res = await fetch(`${API_BASE_URL}/api/notes/share/${idToShare}`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
      const data = await res.json();
      if (res.ok && data.success) {
        setShareUrl(`${window.location.origin}/shared/${data.shareId}`);
        setShowShareModal(true);
      } else { alert(`Failed to share: ${data.error || 'Unknown error'}`); }
    } catch (error) { alert('Could not connect to server.'); } finally { setIsSharing(false); }
  };
  const copyToClipboard = () => { navigator.clipboard.writeText(shareUrl); setCopied(true); setTimeout(() => setCopied(false), 2000); };

  // --- EXPORT LOGIC ---
  const handleExportToNotion = async () => {
    if (!integrationStatus?.notion?.connected) { alert("Notion is not connected."); return; }
    setExportingNotion(true);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_BASE_URL}/api/integrations/notion/pages`, { headers: { 'Authorization': `Bearer ${token}` } });
      if (res.ok) {
        const data = await res.json();
        if (data.pages && data.pages.length > 1) {
          setExportingNotion(false);
          setShowDestinationPicker({ provider: 'notion', data: data.pages });
          return;
        }
        await performNotionExport(data.pages?.[0]?.id);
      } else { await performNotionExport(); }
    } catch (e) { await performNotionExport(); }
  };

  const performNotionExport = async (pageId = null) => {
    setExportingNotion(true); setShowDestinationPicker(null);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_BASE_URL}/api/integrations/notion/export`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          meetingTitle: displayTitle,
          date: getMeetingDate(activeMeeting),
          summary: transcriptData?.summary,
          todos: transcriptData?.todos,
          transcript: transcriptData?.events,
          pageId
        })
      });
      if (res.ok) {
        const data = await res.json();
        if (data.success) {
          setExportSuccessProvider('notion'); setExportSuccessUrl(data.url);
          setTimeout(() => { setExportSuccessUrl(null); setExportSuccessProvider(null); setShowExportMenu(false); }, 5000);
        } else alert('Failed to export: ' + data.message);
      }
    } catch (e) { alert('Export failed.'); } finally { setExportingNotion(false); }
  };

  const handleExportToSlack = async () => {
    if (!integrationStatus?.slack?.connected) { alert("Slack is not connected."); return; }
    setExportingSlack(true);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_BASE_URL}/api/integrations/slack/channels`, { headers: { 'Authorization': `Bearer ${token}` } });
      if (res.ok) {
        const data = await res.json();
        if (data.channels && data.channels.length > 1) {
          setExportingSlack(false);
          setShowDestinationPicker({ provider: 'slack', data: data.channels });
          return;
        }
        await performSlackExport(data.channels?.[0]?.id);
      } else {
        await performSlackExport();
      }
    } catch (e) { await performSlackExport(); }
  };

  const performSlackExport = async (channelId = null) => {
    setExportingSlack(true); setShowDestinationPicker(null);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_BASE_URL}/api/integrations/slack/export`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          meetingTitle: displayTitle,
          date: getMeetingDate(activeMeeting),
          summary: transcriptData?.summary,
          todos: transcriptData?.todos,
          transcript: transcriptData?.events,
          channelId
        })
      });
      if (res.ok) {
        const data = await res.json();
        if (data.success) {
          setExportSuccessProvider('slack'); setExportSuccessUrl(data.url);
          setTimeout(() => { setExportSuccessUrl(null); setExportSuccessProvider(null); setShowExportMenu(false); }, 5000);
        } else alert('Failed to export: ' + data.message);
      }
    } catch (e) { alert('Export failed.'); setExportingSlack(false); }
  };

  const handleBackToList = () => {
    setSelectedMeetingId(null);
    setShowMobileTranscript(false);
  };

  const handleSelectMeeting = (m) => {
    setSelectedMeetingId(m.meeting_id);
    setActiveTab('notes');
    setShowMobileTranscript(false);
  };

  // --- FETCH SUMMARY (PROXY LOGIC) ---
  useEffect(() => {
    if (!activeMeeting?.meeting_id) { setTranscriptData(null); return; }
    if (dataCache.current[activeMeeting.meeting_id]) { setTranscriptData(dataCache.current[activeMeeting.meeting_id]); setLoading(false); return; }

    let isMounted = true;
    setLoading(true);

    const fetchSummary = async () => {
      try {
        const token = localStorage.getItem('token');
        const assetsPromise = fetch(`${API_BASE_URL}/api/transcripts/${activeMeeting.meeting_id}/assets`, { headers: { 'Authorization': `Bearer ${token}` } });

        const summaryPromise = (async () => {
          let res = await fetch(`${SUMMARIZER_PROXY_URL}/${activeMeeting.meeting_id}`, {
            method: 'GET',
            headers: { 'Authorization': `Bearer ${token}` }
          }).catch(err => null);

          if (!res || !res.ok) {
            try {
              await fetch(`${SUMMARIZER_PROXY_URL}/generate`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
                body: JSON.stringify({ meeting_id: activeMeeting.meeting_id })
              });
              await new Promise(r => setTimeout(r, 2000));
            } catch (postErr) { console.error("Generation trigger failed", postErr); }

            res = await fetch(`${SUMMARIZER_PROXY_URL}/${activeMeeting.meeting_id}`, {
              method: 'GET',
              headers: { 'Authorization': `Bearer ${token}` }
            });
          }
          return res;
        })();

        const [assetsRes, summaryRes] = await Promise.all([assetsPromise, summaryPromise]);

        if (isMounted) {
          let combinedData = { events: [], video_url: null, summary_status: 'processing', stopped_early: false, termination_reason: null };
          if (assetsRes.ok) {
            const assetsData = await assetsRes.json();
            const rawTranscript = assetsData.data.transcript || {};
            combinedData = {
              ...combinedData,
              ...rawTranscript,
              events: rawTranscript.events || [],
              video_url: assetsData.data.video_url,
              // Early termination info
              stopped_early: assetsData.stopped_early || false,
              termination_reason: assetsData.termination_reason || null,
              end_time: assetsData.end_time,
              last_stopped_at: assetsData.last_stopped_at
            };
          }
          if (summaryRes && summaryRes.ok) {
            const summaryData = await summaryRes.json();
            combinedData = { ...combinedData, ...summaryData, summary_status: 'completed' };
            if (summaryData.title) {
              const currentTitle = activeMeeting.meeting_title || "";
              if (!currentTitle || currentTitle.toLowerCase().includes("instant") || currentTitle.trim() === "New Meeting") {
                saveMeetingTitle(activeMeeting.meeting_id, summaryData.title);
              }
            }
          }
          dataCache.current[activeMeeting.meeting_id] = combinedData;
          setTranscriptData(combinedData);
        }
      } catch (err) { console.error(err); } finally { if (isMounted) setLoading(false); }
    };
    fetchSummary();
    return () => { isMounted = false; };
  }, [activeMeeting?.meeting_id]);

  const handleJumpTo = (timestampMs) => {
    if (!timestampMs || !transcriptData?.meeting_start_ms) return;
    const sec = (timestampMs - transcriptData.meeting_start_ms) / 1000;
    setActiveTab('video');
    setTimeout(() => { if (videoRef.current) { videoRef.current.currentTime = Math.max(0, sec); videoRef.current.play().catch(() => { }); } }, 100);
  };

  const handleSummaryBlockClick = (block, index) => {
    const sourceIds = block.source_event_ids || [];
    if (activeBlockIndex === index) { setActiveSourceIds([]); setActiveBlockIndex(null); setSearchQuery(''); }
    else {
      setActiveSourceIds(sourceIds); setActiveBlockIndex(index); setSearchQuery('');
      if (window.innerWidth < 768) setShowMobileTranscript(true);
      if (sourceIds.length > 0) setTimeout(() => { const targetId = Object.keys(transcriptRefs.current).find(key => key.startsWith(sourceIds[0])); if (targetId && transcriptRefs.current[targetId]) transcriptRefs.current[targetId]?.scrollIntoView({ behavior: 'smooth', block: 'center' }); }, 100);
    }
  };

  const processedEvents = useMemo(() => parseTranscriptData(transcriptData?.events), [transcriptData?.events]);
  const filteredEvents = useMemo(() => {
    return processedEvents.filter(evt => {
      if (activeSourceIds.length > 0) return activeSourceIds.some(sid => evt.id.startsWith(sid));
      if (searchQuery) { const q = searchQuery.toLowerCase(); return (evt.displayText || '').toLowerCase().includes(q) || (evt.displaySpeaker || '').toLowerCase().includes(q); }
      return true;
    });
  }, [processedEvents, activeSourceIds, searchQuery]);

  const displayTitle = transcriptData?.title || activeMeeting?.meeting_title || "No Engaged Meeting";
  const activeDate = getMeetingDate(activeMeeting);

  return (
    <div className={`flex h-screen w-full ${darkMode ? 'text-white bg-primary' : 'text-slate-900 bg-slate-50'} overflow-hidden select-none`}>
      <style>{`.custom-scrollbar::-webkit-scrollbar { width: 6px; height: 6px; } .custom-scrollbar::-webkit-scrollbar-track { background: transparent; } .custom-scrollbar::-webkit-scrollbar-thumb { background-color: ${darkMode ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)'}; border-radius: 20px; } .custom-scrollbar { scrollbar-width: thin; scrollbar-color: ${darkMode ? 'rgba(255, 255, 255, 0.1) transparent' : 'rgba(0, 0, 0, 0.1) transparent'}; }`}</style>

      {/* MOBILE LIST VIEW */}
      <div className={`
        flex-col border-r flex-shrink-0 h-full transition-all duration-300 w-full
        ${darkMode ? 'bg-primary border-white/5' : 'bg-white border-gray-200'}
        ${selectedMeetingId ? 'hidden' : 'flex md:hidden'} 
      `}>
        <div className="p-5 border-b border-inherit flex items-center justify-between shrink-0">
          <h1 className="font-medium text-lg tracking-tight">Meetings</h1>
          <Filter size={16} className="opacity-40 hover:opacity-100 cursor-pointer" />
        </div>
        <div className="flex-1 overflow-y-auto custom-scrollbar">
          {engagedMeetings.map(m => (
            <button
              key={m.meeting_id}
              onClick={() => { setSelectedMeetingId(m.meeting_id); setActiveTab('notes'); }}
              className={`w-full p-4 text-left border-b border-inherit transition-all relative group hover:bg-gray-100 dark:hover:bg-white/5`}
            >
              <div className="flex justify-between items-start mb-1">
                <span className={`text-[10px] font-medium uppercase tracking-wider ${darkMode ? 'text-secondary' : 'text-secondary'}`}>Summary</span>
                <span className="text-[10px] opacity-40">{new Date(m.engaged_at || m.createdAt).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}</span>
              </div>
              <p className={`text-sm font-medium line-clamp-2 leading-snug mb-1 ${darkMode ? 'text-gray-200' : 'text-gray-800'}`}>{m.meeting_title || "Untitled Meeting"}</p>
              <div className="text-[10px] opacity-50 flex items-center gap-1"><Clock size={10} /> {new Date(m.engaged_at || m.createdAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</div>
            </button>
          ))}
          {engagedMeetings.length === 0 && <div className="p-8 text-center opacity-40 text-sm">No meetings found.</div>}
        </div>
      </div>

      {/* MAIN CONTENT */}
      <div className={`
        flex-1 flex-col min-w-0 h-full relative
        ${selectedMeetingId ? 'flex' : 'hidden md:flex'}
      `}>

        {/* MOBILE NAV HEADER */}
        <div className={`md:hidden p-3 border-b flex items-center gap-2 shrink-0 ${darkMode ? 'bg-primary border-white/5' : 'bg-white border-gray-200'}`}>
          <button onClick={handleBackToList} className="p-2 -ml-2 hover:bg-gray-100 dark:hover:bg-white/10 rounded-full">
            <ArrowLeft size={20} />
          </button>
          <span className="font-medium text-sm truncate flex-1">{displayTitle}</span>
          <div className="flex items-center gap-1">
            <button onClick={generateShareLink} disabled={isSharing} className="p-2 rounded-full hover:bg-gray-100 dark:hover:bg-white/10 text-inherit">
              {isSharing ? <Loader2 size={18} className="animate-spin" /> : <Share2 size={18} />}
            </button>
            <div className="relative">
              <button onClick={() => setShowExportMenu(!showExportMenu)} className="p-2 rounded-full hover:bg-gray-100 dark:hover:bg-white/10 text-inherit">
                <ExternalLink size={18} />
              </button>
              {showExportMenu && (
                <div className={`absolute right-0 top-full mt-2 w-64 rounded-xl shadow-xl border z-50 overflow-hidden ${darkMode ? 'bg-slate-800 border-white/10' : 'bg-white border-gray-200'}`}>
                  <div className={`p-3 border-b ${darkMode ? 'border-white/10' : 'border-gray-100'}`}>
                    <h4 className="text-xs font-medium uppercase opacity-50 mb-2">Export To</h4>
                    <button onClick={handleExportToNotion} disabled={exportingNotion} className={`w-full flex items-center gap-3 p-2 rounded-lg transition-colors text-left ${darkMode ? 'hover:bg-white/5' : 'hover:bg-gray-50'}`}>
                      <div className="w-8 h-8 rounded bg-white flex items-center justify-center shadow-sm shrink-0">
                        {exportingNotion ? <Loader2 size={16} className="animate-spin text-black" /> : <img src="https://upload.wikimedia.org/wikipedia/commons/4/45/Notion_app_logo.png" alt="N" className="w-5 h-5 object-contain" />}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="font-medium text-sm truncate">Notion</div>
                        <div className="text-[10px] opacity-60 truncate">{exportingNotion ? 'Exporting...' : (integrationStatus?.notion?.connected ? 'Connected' : 'Not connected')}</div>
                      </div>
                    </button>
                    <button onClick={handleExportToSlack} disabled={exportingSlack} className={`w-full flex items-center gap-3 p-2 rounded-lg transition-colors text-left mt-2 ${darkMode ? 'hover:bg-white/5' : 'hover:bg-gray-50'}`}>
                      <div className="w-8 h-8 rounded bg-white flex items-center justify-center shadow-sm shrink-0">
                        {exportingSlack ? <Loader2 size={16} className="animate-spin text-black" /> : <img src="/slack.png" alt="S" className="w-5 h-5 object-contain" />}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="font-medium text-sm truncate">Slack</div>
                        <div className="text-[10px] opacity-60 truncate">{exportingSlack ? 'Sending...' : (integrationStatus?.slack?.connected ? 'Post to Channel' : 'Not connected')}</div>
                      </div>
                    </button>
                  </div>
                  {exportSuccessUrl && (
                    <div className={`p-2 ${darkMode ? 'bg-emerald-500/10' : 'bg-emerald-50'}`}>
                      <a href={exportSuccessUrl} target="_blank" rel="noreferrer" className="flex items-center justify-center gap-2 w-full py-2 text-xs font-medium text-emerald-600 hover:underline">
                        Open in {exportSuccessProvider === 'notion' ? 'Notion' : 'Slack'} <ExternalLink size={12} />
                      </a>
                    </div>
                  )}
                </div>
              )}
            </div>
            <button onClick={() => setShowMobileTranscript(!showMobileTranscript)} className={`p-2 rounded-full relative ${showMobileTranscript ? 'bg-secondary text-white' : 'hover:bg-gray-100 dark:hover:bg-white/10'}`}>
              <FileText size={20} />
            </button>
          </div>
        </div>

        {/* DESKTOP HEADER */}
        <div className="hidden md:flex p-8 border-b border-inherit shrink-0 justify-between items-start">
          <div>
            <div className="flex items-center gap-2 text-sm font-medium opacity-60">
              <span className="uppercase tracking-wider text-xs">Meeting</span>
              <span>/</span>
              <span className="truncate max-w-[200px]">{displayTitle}</span>
            </div>
            <div className="flex flex-wrap items-center gap-4 text-sm mt-4">
              <div className="relative" ref={dropdownRef}>
                <button onClick={() => setIsMeetingDropdownOpen(!isMeetingDropdownOpen)} className={`flex items-center gap-2 pl-4 pr-3 py-2 rounded-xl text-xs font-medium border transition-all ${darkMode ? 'bg-white/5 border-white/10' : 'bg-white border-gray-200'}`}>
                  <span className="truncate max-w-[200px] font-medium text-base">{displayTitle}</span>
                  <ChevronDown size={16} />
                </button>
                {isMeetingDropdownOpen && (
                  <div className={`absolute left-0 top-full mt-2 w-80 max-h-96 overflow-y-auto rounded-xl shadow-2xl border z-50 ${darkMode ? 'bg-slate-800 border-white/10' : 'bg-white border-gray-100'}`}>
                    {engagedMeetings.map(m => (
                      <button key={m.meeting_id} onClick={() => { setSelectedMeetingId(m.meeting_id); setIsMeetingDropdownOpen(false); }} className={`w-full text-left px-4 py-3 border-b last:border-0 flex items-center gap-3 ${darkMode ? 'hover:bg-white/5' : 'hover:bg-gray-50'}`}>
                        <div className={`w-1.5 h-1.5 rounded-full shrink-0 ${m.meeting_id === activeMeeting?.meeting_id ? 'bg-secondary' : 'bg-transparent'}`} />
                        <div className="flex-1 min-w-0"><div className="text-sm font-medium truncate">{m.meeting_title}</div><div className="text-[10px] opacity-50">{new Date(m.createdAt).toLocaleDateString()}</div></div>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="flex gap-2 relative">
            <button onClick={generateShareLink} disabled={isSharing} className={`p-2 rounded-full hover:bg-gray-100 dark:hover:bg-white/10 transition-colors flex items-center gap-2`} title="Share">
              {isSharing ? <Loader2 size={18} className="animate-spin" /> : <Share2 size={18} />}
            </button>
            <div className="relative">
              <button onClick={() => setShowExportMenu(!showExportMenu)} className="p-2 hover:bg-gray-100 dark:hover:bg-white/10 rounded-full transition-colors" title="Export">
                <ExternalLink size={18} />
              </button>
              {showExportMenu && (
                <div className={`absolute right-0 top-full mt-2 w-72 rounded-xl shadow-xl border z-50 overflow-hidden ${darkMode ? 'bg-slate-800 border-white/10' : 'bg-white border-gray-200'}`}>
                  <div className={`p-3 border-b ${darkMode ? 'border-white/10' : 'border-gray-100'}`}>
                    <h4 className="text-xs font-medium uppercase opacity-50 mb-2">Export To</h4>
                    <button onClick={handleExportToNotion} disabled={exportingNotion} className={`w-full flex items-center gap-3 p-2 rounded-lg transition-colors text-left ${darkMode ? 'hover:bg-white/5' : 'hover:bg-gray-50'} disabled:opacity-50`}>
                      <div className="w-8 h-8 rounded bg-white flex items-center justify-center shadow-sm shrink-0">{exportingNotion ? <Loader2 size={16} className="animate-spin text-black" /> : <img src="https://upload.wikimedia.org/wikipedia/commons/4/45/Notion_app_logo.png" alt="N" className="w-5 h-5 object-contain" />}</div>
                      <div className="flex-1 min-w-0"><div className="font-medium text-sm truncate">Notion</div><div className="text-[10px] opacity-60 truncate">{exportingNotion ? 'Exporting...' : (integrationStatus?.notion?.connected ? 'Connected' : 'Not connected')}</div></div>
                    </button>
                    <button onClick={handleExportToSlack} disabled={exportingSlack} className={`w-full flex items-center gap-3 p-2 rounded-lg transition-colors text-left mt-2 ${darkMode ? 'hover:bg-white/5' : 'hover:bg-gray-50'} disabled:opacity-50`}>
                      <div className="w-8 h-8 rounded bg-white flex items-center justify-center shadow-sm shrink-0">{exportingSlack ? <Loader2 size={16} className="animate-spin text-black" /> : <img src="/slack.png" alt="S" className="w-5 h-5 object-contain" />}</div>
                      <div className="flex-1 min-w-0"><div className="font-medium text-sm truncate">Slack</div><div className="text-[10px] opacity-60 truncate">{exportingSlack ? 'Sending...' : (integrationStatus?.slack?.connected ? 'Post to Channel' : 'Not connected')}</div></div>
                    </button>
                  </div>
                  {exportSuccessUrl && <div className={`p-2 ${darkMode ? 'bg-emerald-500/10' : 'bg-emerald-50'}`}><a href={exportSuccessUrl} target="_blank" rel="noreferrer" className="flex items-center justify-center gap-2 w-full py-2 text-xs font-medium text-emerald-600 hover:underline">Open in {exportSuccessProvider === 'notion' ? 'Notion' : 'Slack'} <ExternalLink size={12} /></a></div>}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* TABS */}
        <div className={`flex border-b shrink-0 px-8 ${darkMode ? 'border-white/5' : 'border-gray-200'}`}>
          <button onClick={() => setActiveTab('notes')} className={`pb-4 pt-2 border-b-2 font-medium text-sm flex items-center gap-2 mr-8 transition-colors ${activeTab === 'notes' ? 'border-secondary text-secondary' : 'border-transparent opacity-40 hover:opacity-100'}`}><FileText size={16} /> Notes</button>
          <button onClick={() => setActiveTab('discussion')} className={`pb-4 pt-2 border-b-2 font-medium text-sm flex items-center gap-2 mr-8 transition-colors ${activeTab === 'discussion' ? 'border-secondary text-secondary' : 'border-transparent opacity-40 hover:opacity-100'}`}><MessageSquare size={16} /> Discussion</button>
          <button onClick={() => setActiveTab('video')} className={`pb-4 pt-2 border-b-2 font-medium text-sm flex items-center gap-2 transition-colors ${activeTab === 'video' ? 'border-secondary text-secondary' : 'border-transparent opacity-40 hover:opacity-100'}`}><Video size={16} /> Video</button>
        </div>

        {/* CONTENT AREA */}
        <div className="p-8 overflow-y-auto custom-scrollbar flex-1">
          {loading ? <div className="flex items-center justify-center h-full opacity-50"><Loader2 className="animate-spin" /></div> : (
            <>
              {activeTab === 'notes' && transcriptData && (
                <div className="max-w-3xl space-y-8 animate-entry">
                  {/* Early Termination Warning Banner */}
                  {transcriptData.stopped_early && (
                    <div className={`flex items-center gap-3 p-4 rounded-xl border ${darkMode ? 'bg-amber-500/10 border-amber-500/30 text-amber-300' : 'bg-amber-50 border-amber-200 text-amber-800'}`}>
                      <AlertTriangle size={20} className="shrink-0" />
                      <div className="flex-1">
                        <p className="text-sm font-medium">
                          {transcriptData.termination_reason === 'kicked'
                            ? 'Bot was removed from the meeting early'
                            : 'Bot was disengaged before the scheduled end time'}
                        </p>
                        <p className={`text-xs mt-0.5 ${darkMode ? 'opacity-70' : 'opacity-60'}`}>
                          The notes below contain partial data captured until the bot was stopped.
                        </p>
                      </div>
                    </div>
                  )}

                  {(transcriptData.summary || transcriptData.summary_blocks) && (
                    <div>
                      <h3 className={`font-medium text-lg mb-4 ${darkMode ? 'text-white' : 'text-slate-800'}`}>Meeting Summary</h3>
                      <div className={`p-6 rounded-2xl border ${darkMode ? 'bg-white/5 border-white/5' : 'bg-white border-gray-200'}`}>
                        {transcriptData.summary ? (
                          // Render markdown summary beautifully
                          renderMarkdownSummary(transcriptData.summary, darkMode)
                        ) : (
                          // Fallback to summary_blocks if no markdown summary
                          <p className={`text-sm leading-[1.8] opacity-80 font-light`}>
                            {transcriptData.summary_blocks.map((block, i) => {
                              const hasSource = block.source_event_ids?.length > 0;
                              const cleanText = (block.text || block.content || block).replace(/\*\*/g, '').trim();
                              return <span key={i} onClick={() => hasSource && handleSummaryBlockClick(block, i)} className={`${hasSource ? 'cursor-pointer hover:bg-secondary/10 hover:text-secondary' : ''} ${activeBlockIndex === i ? 'bg-secondary/20 text-secondary' : ''} transition-colors rounded px-1`}>{cleanText}. </span>;
                            })}
                          </p>
                        )}
                      </div>
                    </div>
                  )}

                  {transcriptData.todos && transcriptData.todos.length > 0 && (
                    <div>
                      <h3 className={`font-medium text-lg mb-4 flex items-center gap-2 ${darkMode ? 'text-white' : 'text-slate-800'}`}>
                        <CheckCircle2 size={20} className="text-emerald-500" /> Action Items
                      </h3>
                      <div className="space-y-3">
                        {transcriptData.todos.map((todo, i) => (
                          <TodoItemWithAssign
                            key={i}
                            todo={todo}
                            meetingId={activeMeeting?.meeting_id}
                            meetingTitle={displayTitle}
                            teamMembers={teamMembers}
                            darkMode={darkMode}
                            onJumpToSource={() => {
                              const ids = typeof todo === 'object' ? todo.event_ids : [];
                              if (ids && ids.length) handleSummaryBlockClick({ source_event_ids: ids }, `todo-${i}`);
                            }}
                          />
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
              {activeTab === 'discussion' && transcriptData?.transcript?.chat_messages && (
                <div className="max-w-3xl space-y-6 animate-entry">
                  <h3 className={`font-medium text-lg mb-4 flex items-center gap-2 ${darkMode ? 'text-white' : 'text-slate-800'}`}>
                    <MessageSquare size={20} className="text-secondary" /> Meeting Discussion
                  </h3>
                  <div className={`rounded-2xl p-6 border ${darkMode ? 'bg-white/5 border-white/5' : 'bg-white border-gray-200'}`}>
                    <div className="space-y-4">
                      {transcriptData.transcript.chat_messages.map((message, i) => (
                        <div key={i} className={`flex gap-3 ${message.sender === 'user' ? 'flex-row-reverse' : ''}`}>
                          <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-medium shrink-0 ${message.sender === 'user' ? 'bg-secondary text-white' : 'bg-gray-500 text-white'}`}>
                            {message.sender === 'user' ? 'U' : 'AI'}
                          </div>
                          <div className={`max-w-[70%] ${message.sender === 'user' ? 'text-right' : ''}`}>
                            <div className={`inline-block px-4 py-2 rounded-2xl text-sm font-light ${message.sender === 'user' ? (darkMode ? 'bg-secondary text-white' : 'bg-secondary text-white') : (darkMode ? 'bg-white/10 text-gray-100' : 'bg-gray-50 text-gray-800 border border-gray-200')}`}>
                              {message.content || message.text}
                            </div>
                            <div className="text-xs opacity-50 mt-1">
                              {message.timestamp ? new Date(message.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : ''}
                            </div>
                          </div>
                        </div>
                      ))}
                      {transcriptData.transcript.chat_messages.length === 0 && (
                        <div className={`text-center py-8 opacity-50 ${darkMode ? 'text-white' : 'text-gray-500'}`}>
                          <MessageSquare size={32} className="mx-auto mb-2 opacity-30" />
                          <p>No discussion messages found for this meeting.</p>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}
              {activeTab === 'video' && transcriptData?.video_url && (
                <div className="max-w-4xl mx-auto space-y-4 animate-entry">
                  <h3 className={`font-medium text-lg mb-4 flex items-center gap-2 ${darkMode ? 'text-white' : 'text-slate-800'}`}>
                    <Video size={20} className="text-red-500" /> Meeting Recording
                  </h3>
                  <div className="aspect-video bg-black rounded-2xl overflow-hidden border border-white/10">
                    <video ref={videoRef} src={transcriptData.video_url} controls className="w-full h-full object-contain" />
                  </div>
                </div>
              )}
              {activeTab === 'video' && !transcriptData?.video_url && (
                <div className="max-w-4xl mx-auto space-y-4 animate-entry">
                  {/* Early Termination Warning for Video */}
                  {transcriptData?.stopped_early && (
                    <div className={`flex items-center gap-3 p-4 rounded-xl border ${darkMode ? 'bg-amber-500/10 border-amber-500/30 text-amber-300' : 'bg-amber-50 border-amber-200 text-amber-800'}`}>
                      <AlertTriangle size={20} className="shrink-0" />
                      <div className="flex-1">
                        <p className="text-sm font-medium">
                          {transcriptData.termination_reason === 'kicked'
                            ? 'Bot was removed from the meeting early'
                            : 'Bot was disengaged before the scheduled end time'}
                        </p>
                        <p className={`text-xs mt-0.5 ${darkMode ? 'opacity-70' : 'opacity-60'}`}>
                          Recording may not be available due to early termination.
                        </p>
                      </div>
                    </div>
                  )}
                  <div className={`aspect-video rounded-2xl flex items-center justify-center border ${darkMode ? 'bg-slate-900 border-white/10' : 'bg-gray-100 border-gray-200'}`}>
                    <div className={`text-center opacity-50 ${darkMode ? 'text-white' : 'text-gray-500'}`}>
                      <Video size={48} className="mx-auto mb-4 opacity-30" />
                      <p className="text-lg font-medium">No video recording available</p>
                      <p className="text-sm font-light">
                        {transcriptData?.stopped_early
                          ? 'The bot was stopped before the recording could be processed.'
                          : 'The meeting recording could not be found.'}
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* DESKTOP RESIZER HANDLE */}
      <div onMouseDown={startResizing} className="hidden md:flex w-1 cursor-col-resize hover:bg-secondary z-10 opacity-0 hover:opacity-100 items-center justify-center"><div className="h-8 w-1 bg-white/20 rounded-full" /></div>

      {/* RIGHT PANEL: TRANSCRIPT */}
      <div style={{ width: window.innerWidth >= 768 ? sidebarWidth : '100%' }} className={`flex flex-col border-l transition-transform duration-300 ease-in-out ${darkMode ? 'bg-primary border-white/5 text-white' : 'bg-white border-gray-200 text-slate-800'} ${showMobileTranscript ? 'fixed inset-0 z-50 translate-x-0' : 'fixed inset-0 z-50 translate-x-full md:relative md:z-0 md:translate-x-0'}`}>
        {/* HEADER (Mobile) */}
        <div className="md:hidden p-4 border-b border-inherit flex items-center justify-between shrink-0"><h3 className="font-medium text-lg">Transcript</h3><button onClick={() => setShowMobileTranscript(false)} className="p-2 rounded-full hover:bg-gray-100 dark:hover:bg-white/10"><X size={24} /></button></div>
        {/* SEARCH */}
        <div className="p-4 border-b border-inherit"><div className={`flex items-center px-3 py-2 rounded-xl border ${darkMode ? 'bg-white/5 border-white/10' : 'bg-gray-50 border-gray-200'}`}><Search size={14} className="opacity-40 mr-2" /><input type="text" placeholder="Search..." className="bg-transparent outline-none text-xs w-full font-light" value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} /></div></div>
        {/* EVENTS */}
        <div className="flex-1 overflow-y-auto p-4 space-y-4 custom-scrollbar">
          {filteredEvents?.map((evt, idx) => (
            <div key={evt.id || idx} ref={el => transcriptRefs.current[evt.id] = el} className={`flex gap-3 group ${activeSourceIds.includes(evt.id) ? 'bg-secondary/10 -mx-2 px-2 py-2 rounded-lg' : ''}`}>
              <div className={`w-6 h-6 rounded-lg flex items-center justify-center text-[10px] font-medium shrink-0 ${evt.role === 'bot' ? 'bg-secondary text-white' : 'bg-slate-500 text-white'}`}>{evt.role === 'bot' ? 'AI' : evt.displaySpeaker[0]}</div>
              <div className="flex-1 min-w-0">
                <div className="flex justify-between"><span className="text-xs font-medium opacity-70">{evt.displaySpeaker}</span><span onClick={() => handleJumpTo(evt.start_ms)} className="text-[10px] opacity-40 cursor-pointer hover:text-secondary font-mono">Play</span></div>
                <p className="text-sm opacity-80 mt-1 font-light">{evt.displayText}</p>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* SHARE MODAL */}
      {showShareModal && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 p-4 backdrop-blur-sm">
          <div className={`w-full max-w-md p-6 rounded-2xl shadow-2xl border ${darkMode ? 'bg-slate-800 border-white/10 text-white' : 'bg-white border-gray-200 text-gray-900'}`}>
            <h3 className="text-lg font-medium mb-2">Share Note</h3>
            <div className={`flex items-center gap-2 p-2 rounded-lg border mb-4 ${darkMode ? 'bg-black/20 border-white/10' : 'bg-gray-50 border-gray-200'}`}><input type="text" readOnly value={shareUrl} className="bg-transparent flex-1 text-xs outline-none truncate font-light" /><button onClick={() => { navigator.clipboard.writeText(shareUrl); alert('Copied!') }} className="p-1.5 bg-secondary text-white rounded-md hover:bg-blue-600"><Copy size={14} /></button></div>
            <button onClick={() => setShowShareModal(false)} className="w-full py-2 bg-gray-500/10 hover:bg-gray-500/20 rounded-lg text-sm font-medium">Close</button>
          </div>
        </div>
      )}

      {/* --- DESTINATION PICKER MODAL --- */}
      {showDestinationPicker && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 p-4 animate-in fade-in backdrop-blur-sm">
          <div className={`w-full max-w-md p-6 rounded-2xl shadow-2xl border ${darkMode ? 'bg-slate-800 border-white/10 text-white' : 'bg-white border-gray-200 text-gray-900'}`}>
            <h3 className="text-lg font-medium mb-4">
              Select {showDestinationPicker.provider === 'notion' ? 'Notion Page' : 'Slack Channel'}
            </h3>

            <div className="max-h-60 overflow-y-auto custom-scrollbar space-y-2 mb-4">
              {showDestinationPicker.data.map((item) => (
                <button
                  key={item.id}
                  onClick={() => {
                    if (showDestinationPicker.provider === 'notion') {
                      performNotionExport(item.id);
                    } else {
                      performSlackExport(item.id);
                    }
                    setShowDestinationPicker(null); // Close modal
                  }}
                  className={`w-full text-left px-4 py-3 rounded-xl border transition-all flex items-center gap-3 ${darkMode ? 'border-white/5 hover:bg-white/5' : 'border-gray-200 hover:bg-gray-50'}`}
                >
                  <div className="w-8 h-8 rounded bg-gray-100 flex items-center justify-center text-lg">
                    {item.icon || (showDestinationPicker.provider === 'notion' ? '📄' : '#')}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-sm truncate">{item.title || item.name}</div>
                    <div className="text-[10px] opacity-50 truncate">ID: {item.id}</div>
                  </div>
                  <ChevronRight size={16} className="opacity-30" />
                </button>
              ))}
            </div>

            <button
              onClick={() => {
                setShowDestinationPicker(null);
                setExportingNotion(false);
                setExportingSlack(false);
              }}
              className="w-full py-3 bg-gray-500/10 hover:bg-gray-500/20 rounded-xl text-sm font-medium transition-colors"
            >
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default Notes;